import 'signer-app/screenreaders.module.css';
import styles from 'signer-app/signature-modal/upload/upload.module.css';

import React from 'react';
import { Text } from '@dropbox/dig-components/typography';
import { Button } from '@dropbox/dig-components/buttons';
import Dropzone, { FileRejection } from 'react-dropzone';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';

import {
  DROPZONE_ACCEPT_CONFIG,
  MAX_FILE_SIZE,
  VisuallyHiddenFullStop,
} from './upload-constants';
import UploadProgressBar from './upload-progress-bar';

export interface SignatureUploadDropzoneProps {
  isUploading: boolean;
  showFileSizeError: boolean;
  showFileFormatError: boolean;
  isNewSignerApp?: boolean;
  uploadButtonRef: React.RefObject<HTMLButtonElement>;
  onDropAccepted: (files: File[]) => void;
  onDropRejected: (rejections: FileRejection[]) => void;
}

/**
 * Shared dropzone UI component for signature upload.
 * Used by both old (class-based) and new (hook-based) implementations.
 */
export const SignatureUploadDropzone = React.memo<SignatureUploadDropzoneProps>(
  ({
    isUploading,
    showFileSizeError,
    showFileFormatError,
    isNewSignerApp = false,
    uploadButtonRef,
    onDropAccepted,
    onDropRejected,
  }) => {
    return (
      <Dropzone
        onDropAccepted={onDropAccepted}
        onDropRejected={onDropRejected}
        multiple={false}
        disabled={isUploading}
        accept={DROPZONE_ACCEPT_CONFIG}
        maxSize={MAX_FILE_SIZE}
      >
        {({ getRootProps, getInputProps }) => (
          <div
            {...getRootProps()}
            className={classnames(styles.dropzone, {
              [styles.dropzoneV2]: isNewSignerApp,
            })}
            tabIndex={-1}
          >
            {isUploading ? (
              <UploadProgressBar />
            ) : (
              <>
                <Text id="image-upload-description" color="faint">
                  <FormattedMessage
                    id="164a26be3112ac846aa6e3f022afe2d9c59c3f5589b5cb4994fb5a744f78732c"
                    description="Text for an upload dropzone where user can drag and drop an image file containing a photo of their signature."
                    defaultMessage="Drop a photo of your signature here to upload"
                  />
                  <VisuallyHiddenFullStop />
                </Text>
                <Text
                  id="image-upload-acceptable-file-formats"
                  role={showFileFormatError ? 'alert' : undefined}
                  color={showFileFormatError ? 'error' : 'faint'}
                  className={classnames({
                    visuallyHidden: !showFileFormatError,
                  })}
                >
                  <FormattedMessage
                    id="27d887c3aeadf3f77a8b07a59bb53e8c1a0ed242216251f75d6a38dd7660c197"
                    description="Text indicating acceptable file formats for an upload field."
                    defaultMessage="Acceptable file formats: png, jpg, jpeg, bmp, gif"
                  />
                  <VisuallyHiddenFullStop />
                </Text>
                <Text
                  id="image-upload-max-file-size"
                  role={showFileSizeError ? 'alert' : undefined}
                  color={showFileSizeError ? 'error' : 'faint'}
                >
                  <FormattedMessage
                    id="9a09e238c31eb69893d99ea8fbf12d51c0ce53e0f8128486a9612efa18678ee2"
                    description="Text indicating max acceptable file size for an upload field."
                    defaultMessage="Max file size: 40 MB"
                  />
                  <VisuallyHiddenFullStop />
                </Text>
              </>
            )}
            <input data-testid="upload-input" {...getInputProps()} />
            <Button
              ref={uploadButtonRef}
              variant="primary"
              disabled={isUploading}
              className={classnames(
                styles.uploadButton,
                'whitelabel-primary-button',
              )}
              data-qa-ref="signing-modal--image-upload-button"
              data-testid="signing-modal--image-upload-button"
              aria-describedby="image-upload-description image-upload-acceptable-file-formats image-upload-max-file-size"
            >
              <FormattedMessage
                id="b17e9d8b878d780811448b5c0d39a377c9a14568e1a4987166aded71f2df8353"
                description="Text for an upload button which opens the file chooser when clicked."
                defaultMessage="Upload photo"
              />
            </Button>
          </div>
        )}
      </Dropzone>
    );
  },
);
